import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useFlags } from 'launchdarkly-react-client-sdk';
import orderBy from 'lodash/orderBy';
import { useAppThunkDispatch, RootState } from 'state/store';
import {
  fetchTaskTypes,
  selectCanFetchTaskTypes,
  selectTaskTypes,
} from 'state/TasksList/allTasks/allTasksSlice';
import {
  selectFiltersSettingsTypeId,
  createFilterSetting,
  updateFilterSetting,
} from 'state/Settings/Filters/FiltersSlice';
import { TaskListFilters } from 'types/store/tasksList';
import { CENTRALIZED_TASKS_TABLE_TABS } from 'utils/constants/centralizedTasks';
import { useMyTasks } from '../../v2/hooks/useMyTasks/useMyTasks';
import { Toolbar } from 'Organisms/Table/Toolbar';
import TasksFiltersSidePanel from 'Pages/TasksListPage/components/v2/TasksFiltersSidePanel/TasksFiltersSidePanel';
import AppliedFiltersTags from 'Pages/TasksListPage/components/v2/AppliedFiltersTags/AppliedFiltersTags';
import FilterIcon from 'assets/icons/table/filter.svg';
import DownloadIcon from 'assets/icons/table/download.svg';

const MyTasks = () => {
  const dispatch = useAppThunkDispatch();
  const { normalizedTasksState = false, newTaskTypes = false } = useFlags();
  const [shouldDisplayFilterComponent, setShouldDisplayFilterComponent] =
    useState<boolean>(false);

  const { myTasksFilters, handleUpdateFilters, handleSearchFilterChange } =
    useMyTasks({ useNormalizedState: normalizedTasksState });

  const canFetchTaskTypes = useSelector(selectCanFetchTaskTypes);
  const taskTypes = useSelector(selectTaskTypes);

  useEffect(() => {
    if (canFetchTaskTypes) {
      dispatch(fetchTaskTypes(newTaskTypes));
    }
  }, [canFetchTaskTypes, dispatch, newTaskTypes]);

  const typeFilterOptions = useMemo(
    () =>
      orderBy(
        Object.entries(taskTypes).map(([code, name]) => ({
          value: code,
          label: name,
        })),
        'label'
      ),
    [taskTypes]
  );

  const filtersSettingsTypeId = useSelector((state: RootState) =>
    selectFiltersSettingsTypeId(state, CENTRALIZED_TASKS_TABLE_TABS.MY_TASKS)
  );

  const handleSetFiltersSettings = useCallback(
    (filtersSettings: TaskListFilters) => {
      const { search, ...otherFilters } = filtersSettings;
      if (filtersSettingsTypeId) {
        dispatch(
          updateFilterSetting({
            id: filtersSettingsTypeId,
            updateFields: { filter_settings: otherFilters },
          })
        );
      } else {
        dispatch(
          createFilterSetting({
            filter_type: CENTRALIZED_TASKS_TABLE_TABS.MY_TASKS,
            filter_settings: otherFilters,
          })
        );
      }
      handleUpdateFilters(otherFilters);
    },
    [filtersSettingsTypeId, handleUpdateFilters, dispatch]
  );

  const toggleFilterComponent = useCallback(
    () => setShouldDisplayFilterComponent((prev) => !prev),
    []
  );

  const handleExport = useCallback(() => {}, []);

  const actions = useMemo(
    () => [
      {
        id: 'filter',
        label: 'Filter',
        icon: FilterIcon,
        onClick: toggleFilterComponent,
      },
      {
        id: 'export',
        label: 'Export',
        icon: DownloadIcon,
        onClick: handleExport,
      },
    ],
    [toggleFilterComponent, handleExport]
  );

  return (
    <div className='w-full h-full'>
      <TasksFiltersSidePanel
        open={shouldDisplayFilterComponent}
        onClose={toggleFilterComponent}
        filters={myTasksFilters}
        onUpdateFilters={handleSetFiltersSettings}
        hideAssigneeFilter
        typeFilterOptions={typeFilterOptions}
      />
      <Toolbar
        searchProps={{
          value: myTasksFilters.search || '',
          onChange: handleSearchFilterChange,
          placeholder: 'Search',
        }}
        actionsProps={{ actions }}
      />
      <AppliedFiltersTags
        filters={myTasksFilters}
        onUpdateFilters={handleSetFiltersSettings}
        tableName={CENTRALIZED_TASKS_TABLE_TABS.MY_TASKS}
        taskTypeNames={taskTypes}
      />
      <div className='mt-4 p-4 border border-neutral-light rounded'>
        <p className='text-neutral-dark'>Table content will go here.</p>
      </div>
    </div>
  );
};

export default MyTasks;
